import * as Yup from "yup";
import "./reset-password.css";
import { useState } from "react";
import { Formik, Form } from "formik";
import { routes } from "../../routes";
import { useSelector } from "react-redux";
import { Eye, EyeOff } from "lucide-react";
import Toaster from "../../components/Toast/Toast";
import Loading from "../../components/Loader/Loading";
import { useLocation, useNavigate } from "react-router-dom";
import { ResetOldPassword } from "../../api/modules/b2droppie.class";
import BackdropLoader from "../../components/BackdropLoader/BackdropLoader";

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState({
    success: "",
    error: "",
  });

  const isLoading = useSelector((state) => state?.loader?.isLoading);

  const queryParams = new URLSearchParams(location.search);

  const token = queryParams.get("token");
  const email = queryParams.get("email");

  const initialValues = {
    new_password: "",
    new_password_confirmation: "",
  };

  const ResetSchema = Yup.object().shape({
    new_password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters long")
      .matches(/[A-Z]/, "Must contain at least one uppercase letter")
      .matches(/[a-z]/, "Must contain at least one lowercase letter")
      .matches(/[0-9]/, "Must contain at least one number"),

    new_password_confirmation: Yup.string()
      .required("Confirm your password")
      .oneOf([Yup.ref("new_password"), null], "Passwords must match"),
  });

  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const payload = {
        token,
        email,
        new_password: values.new_password,
        new_password_confirmation: values.new_password_confirmation,
      };

      const response = await ResetOldPassword(payload);

      const message = response?.data?.message || "Password reset successful!";

      if (response?.data?.success) {
        setToast({
          error: "",
          success: message,
        });

        setTimeout(() => {
          navigate(routes.HOME);
        }, 3000);
      } else {
        setToast({
          success: "",
          error: message || "Password reset failed!",
        });
      }
    } catch (err) {
      setToast({
        success: "",
        error: err?.message || "An unexpected error occurred",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toaster message={toast} />

      <div className="reset-password-page">
        <div className="reset-password-card">
          <h2 className="reset-title">Change Password</h2>

          <Formik
            initialValues={initialValues}
            validationSchema={ResetSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange }) => (
              <Form>
                {/* Password */}
                <div className="field-group">
                  <label>Password</label>

                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="new_password"
                      placeholder="••••••••"
                      value={values.new_password}
                      onChange={handleChange}
                    />

                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  {errors.new_password && touched.new_password && (
                    <span className="error-text">{errors.new_password}</span>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="field-group">
                  <label>Confirm Password</label>

                  <div className="password-input-wrapper">
                    <input
                      type={showConfirm ? "text" : "password"}
                      name="new_password_confirmation"
                      placeholder="••••••••"
                      value={values.new_password_confirmation}
                      onChange={handleChange}
                    />

                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowConfirm(!showConfirm)}
                    >
                      {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  {errors.new_password_confirmation &&
                    touched.new_password_confirmation && (
                      <span className="error-text">
                        {errors.new_password_confirmation}
                      </span>
                    )}
                </div>

                <button
                  type="submit"
                  className="reset-btn"
                  disabled={isLoading}
                >
                  {isLoading ? <Loading /> : "Reset Password"}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <BackdropLoader show={loading} />
    </>
  );
};

export default ResetPassword;
